import type { ReportSnapshot } from "./snapshot";

/**
 * As páginas do relatório, desenhadas em canvas com as medidas do protótipo
 * (`zPdfPages`): folha A4 a 794 × 1123, margem de 48, faixa azul no topo,
 * rodapé com a data e a numeração. As cores são literais de propósito: a folha
 * é papel impresso, não segue o tema da tela.
 */

const W = 794;
const H = 1123;
const P = 48;
const BLUE = "#2E63FF";
const INK = "#182239";
const MUTED = "#657087";
const LINE = "#E3E8F0";
const DISPLAY = "Sora";
const UI = "Instrument Sans";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
const int = new Intl.NumberFormat("pt-BR");
const cents = (value: number) => brl.format(Math.round(value / 100)).replace(/\s/g, " ");
const reais = (value: number) => brl.format(value).replace(/\s/g, " ");

export type CanvasFactory = () => HTMLCanvasElement;

export function drawReportPages(
  snapshot: ReportSnapshot,
  createCanvas: CanvasFactory = () => document.createElement("canvas"),
): HTMLCanvasElement[] {
  const pages: HTMLCanvasElement[] = [];
  let x!: CanvasRenderingContext2D;
  const s = snapshot;

  const text = (
    value: unknown,
    left: number,
    top: number,
    size = 12,
    color = INK,
    weight: number | string = 400,
    font = UI,
    max = 698,
  ) => {
    x.fillStyle = color;
    x.font = `${weight} ${size}px "${font}", sans-serif`;
    const full = String(value ?? "");
    let cut = full;
    while (x.measureText(cut).width > max && cut.length) cut = cut.slice(0, -1);
    if (cut !== full) cut = `${cut.slice(0, -1)}…`;
    x.fillText(cut, left, top);
  };
  const box = (left: number, top: number, width: number, height: number, color: string, radius = 10) => {
    x.fillStyle = color;
    x.beginPath();
    x.roundRect(left, top, width, height, radius);
    x.fill();
  };
  const rule = (y: number) => {
    x.strokeStyle = LINE;
    x.lineWidth = 1;
    x.beginPath();
    x.moveTo(P, y);
    x.lineTo(W - P, y);
    x.stroke();
  };
  const heading = (title: string, sub: string) => {
    const canvas = createCanvas();
    canvas.width = W * 2;
    canvas.height = H * 2;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Não foi possível preparar o PDF.");
    x = context;
    x.scale(2, 2);
    x.fillStyle = "#FFFFFF";
    x.fillRect(0, 0, W, H);
    box(0, 0, W, 9, BLUE, 0);
    text(s.brand, P, 49, 16, BLUE, 600, DISPLAY);
    text(s.agency, 430, 49, 11, MUTED, 400, UI, 316);
    text(title, P, 101, 28, INK, 600, DISPLAY);
    text(sub, P, 128, 12, MUTED);
    rule(149);
    pages.push(canvas);
  };
  const section = (title: string, y: number, sub?: string) => {
    text(title, P, y, 16, INK, 600, DISPLAY);
    if (sub) text(sub, P, y + 21, 11, MUTED);
  };
  const metric = (label: string, value: string, left: number, top: number, width = 165) => {
    box(left, top, width, 86, "#F3F6FC");
    text(label, left + 14, top + 25, 11, MUTED, 500, UI, width - 28);
    text(value, left + 14, top + 59, value.length > 10 ? 16 : 24, BLUE, 600, DISPLAY, width - 28);
  };
  const bars = (rows: [string, number][], y: number, max: number, format: (value: number) => string) => {
    rows.forEach(([label, value], index) => {
      const top = y + index * 39;
      text(label, P, top + 12, 12, INK, 500, UI, 155);
      box(215, top, 425, 13, "#EDF1F8", 4);
      box(215, top, Math.max(2, (425 * value) / Math.max(1, max)), 13, BLUE, 4);
      text(format(value), 654, top + 12, 11, INK, 500, UI, 92);
    });
  };
  const table = (head: string[], rows: (string | number)[][], y: number, widths: number[]) => {
    const height = 32;
    box(P, y, W - P * 2, height, "#EDF2FB", 5);
    let left = P;
    head.forEach((label, index) => {
      text(label, left + 9, y + 21, 10, MUTED, 600, UI, widths[index] - 18);
      left += widths[index];
    });
    rows.forEach((row, index) => {
      const top = y + height + index * 36;
      if (index % 2 === 0) box(P, top, W - P * 2, 36, "#F8FAFD", 0);
      let cell = P;
      row.forEach((value, column) => {
        text(value, cell + 9, top + 23, 11, INK, 400, UI, widths[column] - 18);
        cell += widths[column];
      });
    });
  };

  // --- Página 1: resumo de desempenho -----------------------------------------
  heading("Resumo de desempenho", `${s.hint} · ${s.model === "simple" ? "Relatório simples" : "Relatório completo"}`);
  (
    [
      ["Receita", cents(s.kpis.revenueCents)],
      ["Novos leads", int.format(s.kpis.leads)],
      ["Negócios ganhos", int.format(s.kpis.won)],
      ["Conversão", s.kpis.conversion],
    ] as const
  ).forEach(([label, value], index) => metric(label, value, P + index * 177, 171));

  section("Visão da receita", 300, "Receita total e Leads · participação das primeiras mensalidades");
  const values = s.series.revenue;
  const high = Math.max(...values, 1);
  const chartY = 344;
  const chartH = 130;
  const chartW = 650;
  for (let index = 0; index < 4; index += 1) {
    const y = chartY + (index * chartH) / 3;
    x.strokeStyle = LINE;
    x.beginPath();
    x.moveTo(P, y);
    x.lineTo(W - P, y);
    x.stroke();
  }
  const step = chartW / Math.max(1, values.length - 1);
  const points = values.map((value, index) => [P + 24 + index * step, chartY + chartH - (value / high) * chartH] as const);
  if (points.length) {
    x.beginPath();
    x.moveTo(points[0][0], chartY + chartH);
    for (const [left, top] of points) x.lineTo(left, top);
    x.lineTo(points[points.length - 1][0], chartY + chartH);
    x.closePath();
    const gradient = x.createLinearGradient(0, chartY, 0, chartY + chartH);
    gradient.addColorStop(0, "#D7E3FF");
    gradient.addColorStop(1, "#F7F9FE");
    x.fillStyle = gradient;
    x.fill();
    x.beginPath();
    points.forEach(([left, top], index) => (index ? x.lineTo(left, top) : x.moveTo(left, top)));
    x.strokeStyle = BLUE;
    x.lineWidth = 2.5;
    x.stroke();
    const leadPoints = s.series.leadRevenue.map(
      (value, index) => [P + 24 + index * step, chartY + chartH - (value / high) * chartH] as const,
    );
    x.beginPath();
    leadPoints.forEach(([left, top], index) => (index ? x.lineTo(left, top) : x.moveTo(left, top)));
    x.strokeStyle = "#8E85FF";
    x.stroke();
    // Com muitos dias, só alguns rótulos cabem embaixo do gráfico.
    const every = Math.max(1, Math.ceil(points.length / 12));
    points.forEach(([left, top], index) => {
      x.fillStyle = BLUE;
      x.beginPath();
      x.arc(left, top, 3, 0, Math.PI * 2);
      x.fill();
      if (index % every === 0) text(s.series.labels[index], left - 15, chartY + chartH + 22, 10, MUTED);
    });
  }

  section("Conversão do funil", 541, s.funnelName);
  bars(
    s.stages.map((stage) => [stage.name, stage.reached]),
    578,
    s.stages[0]?.reached || 1,
    (value) => int.format(value),
  );

  section("Meta do trimestre", 818);
  if (s.goal) {
    text(
      `${cents(s.goal.achievedCents)} de ${cents(s.goal.amountCents)} · ${Math.round((s.goal.achievedCents / Math.max(1, s.goal.amountCents)) * 100)}% da meta`,
      P,
      846,
      14,
      BLUE,
      600,
    );
    text(`Referência trimestral · ${Math.round(s.goal.elapsedRatio * 100)}% do trimestre decorrido`, P, 869, 11, MUTED);
  } else {
    text("Nenhuma meta cadastrada para este trimestre.", P, 846, 13, MUTED);
  }

  section("Clientes em destaque", 915, "LTV acumulado dos clientes apresentados na Home");
  if (!s.top.length) text("Nenhum cliente com recebimento registrado.", P, 960, 12, MUTED);
  s.top.forEach((client, index) => {
    const y = 960 + index * 27;
    text(`${client.name} · ${client.company}`, P, y, 12, INK, 500, UI, 515);
    text(cents(client.ltvCents), 590, y, 12, BLUE, 600, UI, 155);
  });

  if (s.model === "full") {
    // --- Página 2: canais e evolução ------------------------------------------
    heading("Canais e evolução", `${s.hint} · ${s.demo ? "dados demonstrativos do período" : "dados do período"}`);
    section("Receita por canal", 185);
    if (!s.channels.length) text("Nenhuma origem com leads ou receita no período.", P, 222, 12, MUTED);
    bars(
      s.channels.map((row) => [row.channel, row.revenueCents]),
      214,
      Math.max(...s.channels.map((row) => row.revenueCents), 1),
      cents,
    );
    section(
      "Evolução por intervalo",
      455,
      "Total recebido, primeiras mensalidades, cadastros contribuintes e vendas confirmadas",
    );
    // O protótipo tabela um intervalo por linha; com 30 ou 90 dias a tabela
    // agrupa os dias em até sete faixas para caber na folha.
    const buckets = Math.min(7, s.series.labels.length);
    const size = Math.ceil(s.series.labels.length / Math.max(1, buckets));
    const sum = (list: number[], from: number) => list.slice(from, from + size).reduce((a, b) => a + b, 0);
    const intervals = Array.from({ length: Math.ceil(s.series.labels.length / Math.max(1, size)) }, (_, index) => {
      const from = index * size;
      const last = Math.min(from + size, s.series.labels.length) - 1;
      const label = size === 1 ? s.series.labels[from] : `${s.series.labels[from]}–${s.series.labels[last]}`;
      return [
        label,
        cents(sum(s.series.revenue, from)),
        cents(sum(s.series.leadRevenue, from)),
        sum(s.series.leadCounts, from),
        sum(s.series.won, from),
      ];
    });
    table(["Intervalo", "Receita total", "Leads (R$)", "Leads (qtd.)", "Ganhos"], intervals, 494, [100, 170, 170, 130, 128]);
    // A passagem entre etapas continua na folha quando cabe; senão, abre a
    // própria, como o protótipo faz com intervalos longos.
    let passageY = 494 + 32 + intervals.length * 36 + 40;
    if (passageY + 35 + 32 + s.stages.length * 36 > 1040) {
      heading("Passagem entre etapas", `${s.funnelName} · ${s.hint}`);
      passageY = 185;
    }
    section("Passagem entre etapas", passageY, s.funnelName);
    table(
      ["Etapa", "Leads", "Retenção do topo", "Tempo médio"],
      s.stages.map((stage) => [
        stage.name,
        stage.reached,
        `${Math.round((stage.reached / Math.max(1, s.stages[0]?.reached ?? 1)) * 100)}%`,
        stage.averageDays === null ? "—" : `${stage.averageDays.toFixed(1).replace(".", ",")} dias`,
      ]),
      passageY + 35,
      [240, 95, 205, 158],
    );

    // --- Página 3: carteira e operação ----------------------------------------
    heading("Carteira e operação", "Retrato atual dos registros · independente do período do gráfico");
    (
      [
        ["Leads na base", s.portfolio.total],
        ["Ativos", s.portfolio.active],
        ["Ganhos", s.portfolio.won],
        ["Perdidos", s.portfolio.lost],
      ] as const
    ).forEach(([label, value], index) => metric(label, int.format(value), P + index * 177, 176));
    section("Potencial e relacionamento", 307);
    table(
      ["Indicador", "Valor"],
      [
        ["Pipeline ativo", reais(s.portfolio.activeValue)],
        ["Valor dos negócios ganhos", reais(s.portfolio.wonValue)],
        ["Valor dos negócios perdidos", reais(s.portfolio.lostValue)],
        ["LTV acumulado da base", cents(s.portfolio.ltvTotalCents)],
        ["LTV médio de clientes", cents(s.portfolio.ltvClientAverageCents)],
        ["AI Score médio", s.portfolio.heatAverage.toFixed(1).replace(".", ",")],
        ["Sem contato há 7 dias ou mais", s.portfolio.quietWeek],
      ],
      331,
      [490, 208],
    );
    section("Tarefas e atenção", 655);
    table(
      ["Indicador", "Quantidade"],
      [
        ["Tarefas pendentes", s.operation.pending],
        ["Tarefas vencidas", s.operation.late],
        ["Tarefas concluídas", s.operation.done],
        ["Avisos em aberto", s.operation.openNotices],
      ],
      680,
      [490, 208],
    );
    section("Próximos passos", 899);
    if (!s.operation.next.length) text("Nenhuma tarefa pendente.", P, 937, 12, MUTED);
    s.operation.next.forEach((task, index) => {
      text(task.title, P, 937 + index * 29, 12, INK, 500, UI, 485);
      text(task.when, 550, 937 + index * 29, 11, MUTED, 400, UI, 195);
    });

    // --- Detalhamento dos leads, 20 por página ----------------------------------
    for (let start = 0; start < Math.max(1, s.leads.length); start += 20) {
      heading("Detalhamento dos leads", `Carteira · ${s.leads.length} registros · ordenada por LTV`);
      table(
        ["Nome / empresa", "Etapa / situação", "Valor", "LTV", "Score"],
        s.leads
          .slice(start, start + 20)
          .map((lead) => [
            `${lead.name} / ${lead.company}`,
            `${lead.stage} / ${lead.situation}`,
            reais(lead.value),
            cents(lead.ltvCents),
            lead.heat,
          ]),
        177,
        [250, 175, 105, 105, 63],
      );
      if (!s.leads.length) text("Nenhum lead nesta base.", P, 247, 13, MUTED);
    }
  }

  pages.forEach((canvas, index) => {
    const context = canvas.getContext("2d");
    if (!context) return;
    x = context;
    rule(1062);
    text(s.demo ? "CosmosLeads · Dados demonstrativos" : "CosmosLeads", P, 1084, 10, MUTED);
    text(s.generatedAt, 330, 1084, 10, MUTED);
    text(`${index + 1} / ${pages.length}`, 699, 1084, 10, MUTED);
  });
  return pages;
}
